import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { useGetCrmSettings, useUpdateCrmSettings } from '../../hooks/backend/crm'
import { toast } from '../../lib/shadcn/sonner'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

export default function CrmSettingsDialog({ open, onOpenChange, onSaved }: Props) {
  const getSettings = useGetCrmSettings()
  const updateSettings = useUpdateCrmSettings()
  const [sid, setSid] = useState('')
  const [from, setFrom] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    getSettings
      .trigger({}, { skipCache: true })
      .then((data: unknown) => {
        const row = (data ?? {}) as { twilio_account_sid?: string; twilio_from_number?: string }
        setSid(row.twilio_account_sid ?? '')
        setFrom(row.twilio_from_number ?? '')
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateSettings.trigger({ twilio_account_sid: sid, twilio_from_number: from })
      toast.success('SMS settings saved')
      onSaved()
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!saving) onOpenChange(o) }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>SMS Settings</DialogTitle>
          <DialogDescription>
            Configure Twilio so the CRM can send text messages. Find these in your Twilio console.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="twilio-sid">Twilio Account SID</Label>
              <Input id="twilio-sid" value={sid} onChange={e => setSid(e.target.value)} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="twilio-from">From Number</Label>
              <Input id="twilio-from" value={from} onChange={e => setFrom(e.target.value)} placeholder="+18065550000" />
              <p className="text-xs text-muted-foreground">
                A Twilio phone number in E.164 format. The Twilio resource must also have valid credentials configured.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
